import { formatDate } from "helpers/formatDate";
import type { IHistoricoPedidoItem } from "interfaces/loja/pedidoHistorico";
import type { IPedidoGerenciarListItem } from "interfaces/loja/pedido";

/** Evento de cancelamento do pedido (auditoria — ex.: `acao: "CANCELAMENTO"`). */
export function isHistoricoCancelamentoPedido(
  acao: string | null | undefined,
  _categoria?: string | null
): boolean {
  return (
    String(acao ?? "")
      .trim()
      .toUpperCase() === "CANCELAMENTO"
  );
}

/** Rótulo amigável para `HistoricoPedido.categoria`. */
export function labelHistoricoCategoria(
  raw: string | null | undefined
): string {
  const c = String(raw ?? "")
    .trim()
    .toUpperCase();
  const m: Record<string, string> = {
    PEDIDO: "Pedido",
    PAGAMENTO: "Pagamento",
    FATURAMENTO: "Faturamento",
    SEPARACAO: "Separação",
    LOGISTICO: "Logística",
    RETIRADA: "Retirada",
    CANCELAMENTO: "Cancelamento",
  };
  return m[c] || (raw?.trim() ? raw.trim() : "Geral");
}

/** Cor do dot na timeline por categoria (nome MUI). */
export function corDotHistoricoCategoria(
  raw: string | null | undefined
): "primary" | "secondary" | "success" | "info" | "warning" | "error" | "grey" {
  const corHistorico = String(raw ?? "")
    .trim()
    .toUpperCase();
  if (corHistorico === "PAGAMENTO") return "info";
  if (corHistorico === "FATURAMENTO") return "secondary";
  if (corHistorico === "SEPARACAO") return "warning";
  if (corHistorico === "LOGISTICO") return "primary";
  if (corHistorico === "RETIRADA") return "success";
  if (corHistorico === "PEDIDO") return "grey";
  if (corHistorico === "CANCELAMENTO") return "error";
  return "grey";
}

/** Título curto alinhado à timeline de detalhe do pedido. */
export function tituloResumoHistoricoPedido(it: IHistoricoPedidoItem): string {
  if (isHistoricoCancelamentoPedido(it.acao, it.categoria)) {
    return "Cancelamento do pedido";
  }
  return `${labelHistoricoCategoria(it.categoria)} · ${it.acao}`;
}

/** Escolhe o lançamento mais recente quando houver mais de um item. */
export function ultimoHistoricoPedido(
  row: Pick<IPedidoGerenciarListItem, "historicoPedidos">
): IHistoricoPedidoItem | undefined {
  const list = row.historicoPedidos;
  if (!list?.length) return undefined;
  if (list.length === 1) return list[0];
  return [...list].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0];
}

function fmtTransicaoHistorico(
  antes: string | null | undefined,
  depois: string | null | undefined
): string | null {
  const a = antes?.trim() || "—";
  const d = depois?.trim() || "—";
  if (a === "—" && d === "—") return null;
  return `${a} → ${d}`;
}

/** Texto multi-linha para tooltip (data, status, descrição, usuário). */
export function textoTooltipHistoricoPedido(it: IHistoricoPedidoItem): string {
  const lines: string[] = [];
  try {
    lines.push(`Data: ${formatDate(it.createdAt, "dd/MM/yyyy HH:mm:ss")}`);
  } catch {
    lines.push(`Data: ${it.createdAt}`);
  }
  const transicao = fmtTransicaoHistorico(it.statusAntes, it.statusDepois);
  if (transicao) lines.push(`Status: ${transicao}`);
  const desc = it.descricao?.trim();
  if (desc) lines.push(desc);
  const nomeUsuario =
    it.usuario?.nome?.trim() ||
    it.usuario?.email?.trim() ||
    (it.usuario?.id ? `${it.usuario.id.slice(0, 8)}…` : "");
  if (nomeUsuario) lines.push(`Usuário: ${nomeUsuario}`);
  return lines.join("\n");
}
